import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type {
  LinkedTariffItem,
  OperationType,
  PaginatedResponse,
  ServiceLink,
  TariffAgreement,
  TariffChangeRow,
  TariffCoverageRow,
  TariffSpecialty,
  ServiceFacets,
} from '@core/types/api';

/** A page of links as the two-sided table consumes it. */
export interface LinkPage {
  results: ServiceLink[];
  count: number;
}

export interface ListLinksArgs {
  insurer: string;
  page: number;
  pageSize: number;
  ordering?: string;
  search?: string;
  /** Status codes whose label matched the search term (the SPA owns the labels). */
  statusAny?: string[];
  pendingOnly?: boolean;
  /** True: only pending services with a proposal. False: only those with none. */
  suggested?: boolean;
  /** One status, when a tray asks for it. */
  status?: string;
  /** Any of these portfolios (repeated in the query string). */
  scopes?: string[];
  /** Any of these price kinds (repeated in the query string; general price). */
  priceKinds?: string[];
  /** Only services in a cartera: what Mapeos and Tarifas are about. */
  inCartera?: boolean;
  /** Any of these billing areas / groups of the centre (repeated in the query string). */
  billingAreas?: string[];
  groups?: string[];
  /** True: only services already mapped onto an act. False: only those still to map. */
  linked?: boolean;
  specialty?: string;
  /** True: only links with a route on the portal. False: only those without (ADR-0020). */
  routed?: boolean;
  /** The agreement to price the rows at; without it no `agreed_price` is sent. */
  agreement?: string;
}

/** Which decision a batch takes. `linked` confirms each service's own proposal. */
export type LinkDecision = 'linked' | 'not_billed';

/** What a batch changed, and what it could not take. */
export interface BulkDecision {
  decided: number;
  skipped: number;
}

/**
 * What the links table asks the server for.
 *
 * Exported because a filter added to the table's state and forgotten here looks
 * exactly like a server that ignores it.
 */
export function linkParams({
  insurer,
  page,
  pageSize,
  ordering,
  search,
  statusAny,
  pendingOnly,
  suggested,
  status,
  routed,
  scopes,
  priceKinds,
  inCartera,
  billingAreas,
  groups,
  linked,
  specialty,
  agreement,
}: ListLinksArgs): URLSearchParams {
  // A query string rather than a params object: a filter with several values is
  // the same key repeated, which `URLSearchParams` can say and a plain object cannot.
  const params = new URLSearchParams({ insurer, page: String(page), page_size: String(pageSize) });
  if (ordering) params.set('ordering', ordering);
  if (search) params.set('search', search);
  if (statusAny?.length) params.set('status_any', statusAny.join(','));
  if (pendingOnly) params.set('pending_only', 'true');
  if (suggested !== undefined) params.set('suggested', suggested ? 'true' : 'false');
  if (status) params.set('status', status);
  if (routed !== undefined) params.set('routed', routed ? 'true' : 'false');
  for (const scope of scopes ?? []) params.append('scope', scope);
  for (const kind of priceKinds ?? []) params.append('price_kind', kind);
  if (inCartera) params.set('in_cartera', 'true');
  for (const area of billingAreas ?? []) params.append('billing_area', area);
  for (const group of groups ?? []) params.append('group', group);
  if (linked !== undefined) params.set('linked', linked ? 'true' : 'false');
  if (specialty) params.set('specialty', specialty);
  if (agreement) params.set('agreement', agreement);
  return params;
}

/** What the mapping dialog sends: the services, the act, and how it is passed. */
export interface LinkServicesArgs {
  uuids: string[];
  /** The act's uuid on the insurer's current tariff. */
  item: string;
  operationType?: string | null;
  note?: string;
  performable: boolean;
  needsAuthorization: boolean;
  billable: boolean;
  tracksArrivals: boolean;
  suggestionReceipt?: string;
}

export interface MappingSuggestion {
  uuid: string;
  item: LinkedTariffItem | null;
  specialty: { code: string; name: string } | null;
  suggestion_receipt: string;
}
export interface MappingSuggestionRequest {
  services: { uuid: string; specialty: string | null }[];
}

export type MappingChoiceArgs = Omit<LinkServicesArgs, 'uuids'> & { uuid: string };

function mappingBody({ item, operationType, note = '', ...flags }: Omit<LinkServicesArgs, 'uuids'>) {
  return { item, note, operation_type: operationType ?? null,
    performable: flags.performable, needs_authorization: flags.needsAuthorization,
    billable: flags.billable, tracks_arrivals: flags.tracksArrivals, suggestion_receipt: flags.suggestionReceipt };
}

export interface SearchItemsArgs {
  insurer: string;
  search?: string;
  /** Narrows the first, empty-search list to the service's own specialty. */
  specialty?: string;
}

/** How one service is billed as one act: what `POST links/<uuid>/link/` takes. */
export interface LinkServiceArgs {
  uuid: string;
  item: string;
  operationType?: string | null;
  performable?: boolean;
  needsAuthorization?: boolean;
  billable?: boolean;
  tracksArrivals?: boolean;
  note?: string;
}

export interface DecideLinksArgs {
  uuids: string[];
  decision: LinkDecision;
  operationType?: string | null;
  note: string;
}

export interface ListChangesArgs {
  tariff: string;
  page: number;
  pageSize: number;
  ordering?: string;
  search?: string;
  kind?: string;
}

export interface ChangePage {
  results: TariffChangeRow[];
  count: number;
}

function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  return (response as PaginatedResponse<T> | null)?.results ?? [];
}

/**
 * Tariffs API: how much of the centre's services each insurer's catalogue covers,
 * and the decisions.
 *
 * COMPANY-scoped: a link is one company's reading of its own contract, so the cache is
 * dropped when the active organization OR the active company changes
 * (`TENANT_SCOPED_APIS` and `COMPANY_SCOPED_APIS`).
 */
export const tariffsApi = createApi({
  reducerPath: 'tariffsApi',
  baseQuery,
  tagTypes: ['Coverage', 'Link', 'Change', 'PortalForm'],
  endpoints: (builder) => ({
    /** The aggregator's cascade for the insurer (ADR-0020): specialties, act lists, doctors. */


    /** Where the portal takes a batch of services: the specialty and, if it has several, the act list. */

    getCoverage: builder.query<TariffCoverageRow[], void>({
      query: () => ({ url: '/api/tariffs/coverage/', method: 'GET' }),
      transformResponse: (response: unknown) => unwrapList<TariffCoverageRow>(response),
      providesTags: ['Coverage'],
    }),

    /** The areas and groups of the company's cartera, for the filters of this screen. */
    getServiceFacets: builder.query<ServiceFacets, void>({
      query: () => ({ url: '/api/tariffs/services/facets/', method: 'GET' }),
    }),

    /** Set the centre's price for these services to what the insurer's tariff pays. */
    syncHisPrices: builder.mutation<{ synced: number; skipped: number }, string[]>({
      query: (uuids) => ({ url: '/api/tariffs/links/sync-price/', method: 'POST', body: { uuids } }),
      invalidatesTags: ['Link', 'Coverage'],
    }),

    /** Undo the mapping of every listed service, putting them back in the queue. */
    resetLinks: builder.mutation<{ reset: number }, string[]>({
      query: (uuids) => ({ url: '/api/tariffs/links/reset/', method: 'POST', body: { uuids } }),
      invalidatesTags: ['Link', 'Coverage'],
    }),

    /**
     * Map every listed service onto one act of the insurer, and say how it is
     * passed. One service or fifty take the same endpoint: the dialog is the same.
     */
    linkServices: builder.mutation<{ linked: number }, LinkServicesArgs | { choices: MappingChoiceArgs[] }>({
      query: (args) => ({
        url: '/api/tariffs/links/link/', method: 'POST',
        body: 'choices' in args
          ? { choices: args.choices.map(({ uuid, ...choice }) => ({ uuid, ...mappingBody(choice) })) }
          : { uuids: args.uuids, ...mappingBody(args) },
      }),
      invalidatesTags: ['Link', 'Coverage'],
    }),

    suggestMappings: builder.mutation<MappingSuggestion[], MappingSuggestionRequest>({
      query: (body) => ({ url: '/api/tariffs/links/suggest/', method: 'POST', body, timeout: 60000 }),
    }),

    getLink: builder.query<ServiceLink, string>({
      query: (uuid) => `/api/tariffs/links/${uuid}/`,
      providesTags: ['Link'],
    }),

    listLinks: builder.query<LinkPage, ListLinksArgs>({
      query: (args) => ({ url: `/api/tariffs/links/?${linkParams(args)}`, method: 'GET' }),
      transformResponse: (response: PaginatedResponse<ServiceLink>) => ({
        results: response?.results ?? [],
        count: response?.count ?? 0,
      }),
      providesTags: ['Link'],
    }),

    /** The acts of the insurer's current tariff, for the picker. */
    /** The specialties of the insurer's current tariff: the first half of the act picker. */
    listTariffSpecialties: builder.query<TariffSpecialty[], string>({
      query: (insurer) => ({ url: '/api/tariffs/items/specialties/', method: 'GET', params: { insurer } }),
      transformResponse: (response: unknown) => unwrapList<TariffSpecialty>(response),
    }),

    searchItems: builder.query<LinkedTariffItem[], SearchItemsArgs>({
      query: ({ insurer, search, specialty }) => ({
        url: '/api/tariffs/items/',
        method: 'GET',
        params: {
          insurer,
          current: 'true',
          // The general tariff only: an agreement's snapshot repeats the same acts,
          // and a mapping is the same under every agreement of the insurer.
          general: 'true',
          // One specialty at a time: the dialog lists its acts in a dropdown. A
          // specialty can hold more than a page (ASISA's analyses hold 1897), so the
          // dialog also sends what the reader types and the server narrows.
          page_size: 500,
          ...(search ? { search } : {}),
          ...(specialty ? { specialty } : {}),
        },
      }),
      transformResponse: (response: unknown) => unwrapList<LinkedTariffItem>(response),
    }),

    listAgreements: builder.query<TariffAgreement[], string>({
      query: (insurer) => ({ url: '/api/tariffs/agreements/', method: 'GET', params: { insurer } }),
      transformResponse: (response: unknown) => unwrapList<TariffAgreement>(response),
    }),

    listOperationTypes: builder.query<OperationType[], string>({
      query: (insurer) => ({ url: '/api/tariffs/operation-types/', method: 'GET', params: { insurer } }),
      transformResponse: (response: unknown) => unwrapList<OperationType>(response),
    }),

    listChanges: builder.query<ChangePage, ListChangesArgs>({
      query: ({ tariff, page, pageSize, ordering, search, kind }) => ({
        url: '/api/tariffs/changes/',
        method: 'GET',
        params: {
          tariff,
          page,
          page_size: pageSize,
          ...(ordering ? { ordering } : {}),
          ...(search ? { search } : {}),
          ...(kind ? { kind } : {}),
        },
      }),
      transformResponse: (response: PaginatedResponse<TariffChangeRow>) => ({
        results: response?.results ?? [],
        count: response?.count ?? 0,
      }),
      providesTags: ['Change'],
    }),

    /** Somebody has read the changes of this snapshot. */
    reviewTariff: builder.mutation<unknown, string>({
      query: (uuid) => ({ url: `/api/tariffs/${uuid}/review/`, method: 'POST' }),
      invalidatesTags: ['Coverage', 'Change'],
    }),





    /** One decision over the services the reader ticked; see `bulk_decide` in the API. */

  }),
});

export const {
  useSuggestMappingsMutation,
  useLazyGetLinkQuery,
  useGetServiceFacetsQuery,
  useListTariffSpecialtiesQuery,
  useLinkServicesMutation,
  useResetLinksMutation,
  useSyncHisPricesMutation,
  useGetCoverageQuery,
  useListLinksQuery,
  useSearchItemsQuery,
  useListAgreementsQuery,
  useListOperationTypesQuery,
  useListChangesQuery,
  useReviewTariffMutation,
} = tariffsApi;
